import * as React from "react";
import { FC, PropsWithChildren, useContext, useEffect, useMemo } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import {
  AvailablePaymentCurrency,
  UserPermissions,
  UserRoles,
  AmplitudeVariantValue as AmplitudeVariantValueCommon,
  getCurrencyFromCountry,
  OrganizationCountry,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { SessionContext } from "../contexts/SessionContext";
import { enableAuthenticator } from "../dashboard-pages/SignIn/useSignIn";
import { ActivationStepId } from "../features/StepperCheckList/ActivationCheckList/useActivationCard";
import { getAllProperties, identifyAmplitudeUser } from "../helpers/amplitude";
import { isOrganizationRoute } from "../helpers/auth";
import { setInLocalStorage } from "../helpers/localStorage";
import { PageLoadingContext } from "../providers/PageLoadingProvider";

import { GetCurrentUserFrontendObject } from "./types";

import { trpc } from "@/helpers/trpc";

export interface CurrentUserContextValues {
  currentUser: GetCurrentUserFrontendObject | null | undefined;
  userIsLoading: boolean;
  refetchUser: () => Promise<void>;
  isOrganization: boolean;
  isOrganizationOwner: boolean;
  isAuthenticated: boolean;
  userRole?: UserRoles | null;
  userPermissions: UserPermissions[];
  organization?: GetCurrentUserFrontendObject["currentOrganization"];
  refetchOrganization: () => Promise<void>;
  isOrganizationCountryUnitedStates: boolean;
  isOrganizationCountryCanada: boolean;
  isOrganizationCountryGreatBritain: boolean;
  isOrganizationOnboarded: boolean;
  hasStripeCustomFlowStarted: boolean;
  isStripeCustomAccountActive: boolean;
  organizationCurrency: AvailablePaymentCurrency;
  connectedAsAdministrator: boolean;
  isMigratedOnStytch: boolean;
  zeffyAdministrator: boolean;
  isSendingEmailForbidden: boolean;
  firstPaymentDate: Date | null;
  organizationHasFinishZeffyOnboarding: boolean;
  displayActivationCheckList: boolean;
  isEngagementCheckListActive: boolean;
  engagementTourActive: boolean;
  isEngagementCheckListTerminated: boolean;
}

export const defaultValues = {
  currentUser: {} as GetCurrentUserFrontendObject,
  userIsLoading: false,
  refetchUser: async () => {},
  isOrganization: false,
  isOrganizationOwner: false,
  isAuthenticated: false,
  userRole: UserRoles.Owner,
  userPermissions: [],
  organization: undefined,
  refetchOrganization: async () => {},
  isOrganizationCountryUnitedStates: false,
  isOrganizationCountryCanada: true,
  isOrganizationCountryGreatBritain: false,
  isOrganizationOnboarded: false,
  hasStripeCustomFlowStarted: false,
  isStripeCustomAccountActive: true,
  organizationCurrency: AvailablePaymentCurrency.Usd,
  connectedAsAdministrator: false,
  isMigratedOnStytch: false,
  isSendingEmailForbidden: false,
  firstPaymentDate: null,
  zeffyAdministrator: false,
  organizationHasFinishZeffyOnboarding: false,
  displayActivationCheckList: false,
  isEngagementCheckListActive: false,
  engagementTourActive: false,
  isEngagementCheckListTerminated: false,
};

export const CurrentUserContext = React.createContext<CurrentUserContextValues>(defaultValues);

export const useCurrentUserContext = (): CurrentUserContextValues => {
  return useContext(CurrentUserContext);
};

declare global {
  interface Window {
    clarity?: (event: string, id: string, properties?: Record<string, unknown>) => void;
    identifyCannyUser?: (
      appId: string,
      user: { email: string; firstName: string; lastName: string; id: string },
      organization?: { id: string; name: string }
    ) => void;
  }
}

export const CurrentUserProvider: FC<PropsWithChildren> = ({ children }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const query = Object.fromEntries(searchParams.entries());
  const targetOrganizationId = query.targetOrganizationId ? (query.targetOrganizationId as string) : null;
  const { mutateAsync: updateAccessTokenWithTargetedOrganization } =
    trpc.updateAccessTokenWithTargetedOrganization.useMutation();

  const {
    data: getUserTrpc,
    isLoading,
    refetch,
  } = enableAuthenticator()
    ? trpc._getFrontendCurrentUser.useQuery(undefined, {
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: false,
      })
    : trpc.getFrontendCurrentUser.useQuery(undefined, {
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: false,
      });

  // Set isConnectedOnDashboard in localStorage
  useEffect(() => {
    if (getUserTrpc?.id) {
      setInLocalStorage?.("isConnectedOnDashboard", "true");
    }
  }, [getUserTrpc]);

  // If targetOrganizationId is present, update the access token with the targeted organization
  useEffect(() => {
    const updateTargetOrganization = async () => {
      if (targetOrganizationId) {
        await updateAccessTokenWithTargetedOrganization({ targetOrganizationId });
        refetch();
      }
    };
    updateTargetOrganization();
  }, [targetOrganizationId, refetch, updateAccessTokenWithTargetedOrganization]);

  const { setIsAdminTheme, setIsFraudulent } = useContext(SessionContext);
  const { setGetCurrentUserLoading } = useContext(PageLoadingContext);

  const currentUser = getUserTrpc;
  const isAuthenticated = Boolean(currentUser?.id);

  const organization = currentUser?.currentOrganization;
  const isOrganization = Boolean(isAuthenticated && organization);
  const organizationCurrency = getCurrencyFromCountry(organization?.country);
  const isSendingEmailForbidden = Boolean(!organization?.isStripeCustomAccountActive || organization.blockEmails);
  const isOrganizationOwner = useMemo(
    () => Boolean(organization && currentUser?.role === UserRoles.Owner),
    [organization, currentUser?.role]
  );

  const { mutateAsync: initializeNpoContextOnSessionStart } = trpc.initializeNpoContextOnSessionStart.useMutation();

  // Initialize NPO context generation on first render
  useEffect(() => {
    const initializeNpoContext = async () => {
      if (organization?.id && !organization?.npoContext && organization?.isStripeCustomAccountActive) {
        await initializeNpoContextOnSessionStart();
      }
    };
    initializeNpoContext();
  }, [
    organization?.id,
    organization?.npoContext,
    organization?.isStripeCustomAccountActive,
    initializeNpoContextOnSessionStart,
  ]);

  const userRole = useMemo(
    () =>
      isOrganizationOwner
        ? UserRoles.Owner
        : isOrganization
          ? UserRoles.Member
          : isAuthenticated
            ? UserRoles.Donor
            : null,
    [isAuthenticated, isOrganization, isOrganizationOwner]
  );

  const userPermissions = currentUser?.permissions || [];
  const isOrganizationView = isOrganizationRoute(router.pathname);

  useEffect(() => {
    const initializeUser = async () => {
      if (currentUser) {
        await identifyAmplitudeUser({
          currentUser,
          organization: isOrganizationView ? organization : null,
        });

        datadogLogs.setUser(currentUser);
        if (organization) {
          datadogLogs.setGlobalContext({ organization });
        }
      }

      if (organization?.fraudulent) {
        setIsFraudulent(true);
      }
    };
    initializeUser();
  }, [currentUser, isOrganizationView, organization, setIsFraudulent]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.clarity && currentUser && currentUser.id) {
      // eslint-disable-next-line no-console
      console.log("identify clarity", currentUser.id);
      window.clarity("identify", currentUser.id);

      const props = getAllProperties({
        currentUser,
        organization: isOrganizationView ? organization : null,
      });
      // Set each property as a Clarity tag, skipping null/undefined
      Object.entries(props).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          window.clarity!("set", key, value as never);
        }
      });
    }
  }, [currentUser, isOrganizationView, organization, organization?.id]);

  const userIsLoading = isLoading;
  const organizationHasFinishZeffyOnboarding = Boolean(organization?.hasFinishZeffyOnboarding);

  useEffect(() => {
    setIsAdminTheme(Boolean(currentUser?.connectedAsAdministrator));
  }, [currentUser?.connectedAsAdministrator, setIsAdminTheme]);

  const isActivationCheckListActive = organization?.activationChecklistExperiment === AmplitudeVariantValueCommon.B;

  const isActivationCheckListTerminated = Boolean(organization?.hasDismissActivationPayoutSuccessCard);
  const isEngagementCheckListActive = organization?.engagementChecklistExperiment === AmplitudeVariantValueCommon.B;
  const isEngagementCheckListTerminated = Boolean(
    organization?.engagementCheckListStepChecked?.includes(ActivationStepId.FinalSuccess)
  );

  const isOrganizationOnboarded = Boolean(organization?.firstPaymentDate && organization?.isStripeCustomAccountActive);

  const displayActivationCheckList =
    (!isEngagementCheckListActive && !isActivationCheckListActive && !isOrganizationOnboarded) ||
    (isEngagementCheckListActive && !isEngagementCheckListTerminated) ||
    (isActivationCheckListActive && !isActivationCheckListTerminated);

  const engagementTourActive = Boolean(
    (isActivationCheckListActive || isEngagementCheckListActive) && isOrganizationView
  );

  useEffect(() => {
    setGetCurrentUserLoading(userIsLoading);
  }, [setGetCurrentUserLoading, userIsLoading]);

  return (
    <CurrentUserContext.Provider
      value={{
        displayActivationCheckList,
        currentUser: currentUser as GetCurrentUserFrontendObject,
        userIsLoading,
        refetchUser: async () => {
          await refetch();
        },
        isOrganization,
        isOrganizationOwner,
        isAuthenticated,
        userRole,
        userPermissions,
        organization: organization || undefined,
        refetchOrganization: async () => {
          await refetch();
        },
        isOrganizationCountryUnitedStates: organization?.country === OrganizationCountry.UnitedStates,
        isOrganizationCountryCanada: organization?.country === OrganizationCountry.Canada,
        isOrganizationCountryGreatBritain: organization?.country === OrganizationCountry.GreatBritain,
        isOrganizationOnboarded,
        hasStripeCustomFlowStarted: Boolean(organization?.hasStripeCustomFlowStarted),
        isStripeCustomAccountActive: Boolean(organization?.isStripeCustomAccountActive),
        firstPaymentDate: organization?.firstPaymentDate ? new Date(organization?.firstPaymentDate) : null,
        organizationCurrency,
        connectedAsAdministrator: Boolean(currentUser?.connectedAsAdministrator),
        isMigratedOnStytch: Boolean(currentUser?.isMigratedOnStytch),
        zeffyAdministrator: Boolean(currentUser?.zeffyAdministrator),
        isSendingEmailForbidden,
        organizationHasFinishZeffyOnboarding,
        isEngagementCheckListActive,
        engagementTourActive,
        isEngagementCheckListTerminated,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};
